import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import { signalerErreur } from '../../../components/Utils/signalerErreur';
import Select from "react-select";
import { differenceInDays, parseISO } from 'date-fns';
import { notify } from '../../../components/Utils/Notifications';
import ActionDropdown from '../../../components/Utils/ActionDropdown';
import { FaRegTrashAlt } from 'react-icons/fa';
import AsyncSelect from "react-select/async"; 
import EmployeeSelect from "../../../components/Formulaire/EmployeeSelect";


const AbsenceForm = ({ setIsDrawerOpen, id, refreshAbsenceList, refreshTrigger }) => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        selectedEmployee: null,
        date: "",
        date_end: "",
        hours: 8,
        absenceType: null,
        description: "",
    });
    const [isForfait, setIsForfait] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [nombreJours, setNombreJours] = useState('');

    const { date, date_end, hours, absenceType, selectedEmployee, description } = formData;
    const [absenceTypes, setAbsenceTypes] = useState([]);
    const [companyConfig, setCompanyConfig] = useState({
        absence_count_weekends: true,
        absence_count_public_holidays: true,
    });

    useEffect(() => {
        apiService.getElement('/company/info')
            .then(res => {
                if (res.data) {
                    setCompanyConfig({
                        absence_count_weekends: res.data.absence_count_weekends ?? true,
                        absence_count_public_holidays: res.data.absence_count_public_holidays ?? true,
                    });
                }
            })
            .catch(err => console.error("Error loading company info for absence form:", err));
    }, []);
    
    // utiliser la meme logique que pour employeeSelect pour supprimer le fetch absenceTypes
    const fetchAbsenceTypes = useCallback(async () => {
        try {
            const response = await apiService.getElement('/absence_types');
            if (response.data && response.data.data) {
                const typesOptions = response.data.data.map(type => ({
                    value: type.category,
                    label: type.name,
                    id: type.id
                }));
                setAbsenceTypes(typesOptions);
            }
        } catch (error) {
            console.error('Error fetching absence types:', error);
            notify(t('absenceForm.fetchTypesError'), '', 3);
        }
    }, []);

    const updateFormData = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    }, []);

    const calculateDays = useCallback(() => {
        if (date && date_end) {
            const startDate = parseISO(date);
            const endDate = parseISO(date_end);

            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || endDate < startDate) {
                return "0";
            }

            if (date === date_end && hours < 8) {
                return (hours / 8).toFixed(2);
            }

            let daysCount = 0;
            let cur = new Date(startDate);

            while (cur <= endDate) {
                const dayOfWeek = cur.getDay(); // 0 = Dimanche, 6 = Samedi
                const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6);

                if (!companyConfig.absence_count_weekends && isWeekend) {
                    cur.setDate(cur.getDate() + 1);
                    continue;
                }

                daysCount += 1;
                cur.setDate(cur.getDate() + 1);
            }

            return daysCount.toFixed(2);
        }
        return "0";
    }, [date, date_end, hours, companyConfig]);

        // enforce date_end not before date
        useEffect(() => {
            if (!date || !date_end) return;
            try {
                const d = parseISO(date);
                const de = parseISO(date_end);
                if (isNaN(d.getTime()) || isNaN(de.getTime())) return;
                if (de < d) {
                    // reset invalid end date
                    updateFormData('date_end', '');
                    notify(t('absenceForm.endDateBeforeStartError'), '', 3);
                }
            } catch (err) {
                // ignore parse errors
            }
        }, [date]);

    useEffect(() => {
        fetchAbsenceTypes();
    }, [fetchAbsenceTypes, refreshTrigger]); // Ajouter refreshTrigger comme dépendance

    useEffect(() => {
        if (id) {
            apiService.getElementById('/absences', id)
                .then(response => {
                    const absenceData = response.data.absence;
                    const absenceTypeValue = absenceTypes.find(type => type.id === absenceData.absenceType) || null;
                    setFormData({
                        selectedEmployee: {
                            value: absenceData.employee_id,
                            label: absenceData.employee_name
                        },
                        date: absenceData.date || "",
                        date_end: absenceData.date_end || "",
                        hours: absenceData.hours || 8,
                        absenceType: absenceTypeValue,
                        description: absenceData.description || ""
                    });
                    setIsForfait(Boolean(absenceData.saisie_forfait));
                    setSelectedMonth(
                        absenceData.mois_annee
                            ? absenceData.mois_annee.split('/').reverse().join('-')
                            : new Date().toISOString().slice(0, 7)
                    );
                    setNombreJours(
                        absenceData.nombre_jours !== null && absenceData.nombre_jours !== undefined
                            ? String(absenceData.nombre_jours)
                            : ''
                    );
                })
                .catch(error => {
                    console.error('Error fetching absence data:', error);
                    notify(t('absenceForm.fetchDataError'), '', 3);
                });
        } else {
            setFormData({
                selectedEmployee: null,
                date: "",
                date_end: "",
                hours: 8,
                absenceType: null,
                description: ""
            });
            setIsForfait(false);
            setSelectedMonth(new Date().toISOString().slice(0, 7));
            setNombreJours('');
        }
    }, [fetchAbsenceTypes, id]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!isForfait) {
            // Validate dates
            if (date && date_end) {
                try {
                    const d = parseISO(date);
                    const de = parseISO(date_end);
                    if (isNaN(d.getTime()) || isNaN(de.getTime())) return notify(t('absenceForm.invalidDateFormat'), '', 3);
                    if (de < d) return notify(t('absenceForm.endDateMustBeAfterStart'), '', 3);
                } catch (err) {
                    signalerErreur(err, 'AbsenceForm.handleSubmit');
                    return notify(t('absenceForm.invalidDateFormat'), '', 3);
                }
            }
        }

        const updatedFormData = {
            selectedEmployee: formData.selectedEmployee.value || null,
            employee_name: formData.selectedEmployee.label || "",
            absenceType: formData.absenceType?.id || null,
            description: formData.description || ""
        };

        if (isForfait) {
            updatedFormData.mois_annee = selectedMonth.split('-').reverse().join('/');
            updatedFormData.nombre_jours = parseFloat(String(nombreJours).replace(',', '.')) || 0;
        } else {
            updatedFormData.date = formData.date;
            updatedFormData.date_end = formData.date_end || formData.date;
            updatedFormData.hours = parseFloat(formData.hours) || 8;
        }

        console.log('Form Data Submitted:', updatedFormData);

        if (id) {
            apiService.updateElementById(`/absences`, id, updatedFormData)
                .then(response => {
                    if (response.data?.error) {
                        notify(response.data.error, '', 3);
                        return;
                    }
                    notify(t('absenceForm.updateSuccess'), '', 2);
                    setIsDrawerOpen(false);
                    refreshAbsenceList();
                })
                .catch(error => {
                    console.error('Error updating absence:', error);
                    notify(error.userMessage || t('absenceForm.updateError'), '', 3);
                }
                );
        } else {
            apiService.saveElement(`/absence`, updatedFormData)
                .then(response => {
                    if (response.data?.error) {
                        notify(response.data.error, '', 3);
                        return;
                    }
                    notify(t('absenceForm.createSuccess'), '', 2);
                    setIsDrawerOpen(false);
                    refreshAbsenceList();
                })
                .catch(error => {
                    console.error('Error creating absence:', error);
                    notify(error.userMessage || t('absenceForm.createError'), '', 3);
                });
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t('absenceForm.deleteConfirm'))) {
            return;
        }
        try {
            const updatedFormData = {
                active: false,
            };
            await apiService.updateElementById(`/absences`, id, updatedFormData);
            notify(t('absenceForm.deleteSuccess'), '', 2);
            setIsDrawerOpen(false);
            refreshAbsenceList();
        } catch (error) {
            console.error('Error deleting absence:', error);
            notify(error.userMessage || t('absenceForm.deleteError'), '', 3);
        }
    };
    const [loading, setLoading] = useState(false);

    const dropdownActions = [
            {
                label: t('hspForm.delete'),
                onClick: handleDelete,
                disabled: loading || !id,
                color: 'text-red-500',
                icon: <FaRegTrashAlt size={16} />
            }
        ]

    return (
        <div>
            <div className="p-6 h-screen w-full max-w-3xl bg-white text-base-content flex flex-col">
            <div>
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold mb-4">{id ? t('absenceForm.editTitle') : t('absenceForm.addTitle')}</h2>
                    {id && (
                        <ActionDropdown actions={dropdownActions} />
                    )}
                </div>
            </div>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">{t('hspForm.employeeName')}</label>
                        <EmployeeSelect
                            value={selectedEmployee}
                            onChange={option => updateFormData('selectedEmployee', option)}
                            placeholder={t('absenceForm.employeeNamePlaceholder')}
                            minInputLength={2}
                        />
                    </div>

                    <div className="mb-4">
                        <Select
                            id="absenceType"
                            options={absenceTypes.length > 0 ? absenceTypes : []}
                            value={absenceType}
                            onChange={(value) => updateFormData('absenceType', value)}
                            placeholder={t('absenceForm.chooseAbsenceType')}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-2">{t('absenceForm.entryMode')}</label>
                        <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 shadow-sm" role="tablist" aria-label={t('absenceForm.entryMode')}>
                            <button
                                type="button"
                                role="tab"
                                aria-pressed={!isForfait}
                                onClick={() => setIsForfait(false)}
                                className={`px-4 py-2 rounded-md transition text-sm font-medium ${!isForfait ? 'bg-[--color-primary] text-white shadow' : 'text-gray-700 hover:bg-white'}`}
                            >
                                {t('absenceForm.byPeriod')}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-pressed={isForfait}
                                onClick={() => setIsForfait(true)}
                                className={`ml-2 px-4 py-2 rounded-md transition text-sm font-medium ${isForfait ? 'bg-[--color-primary] text-white shadow' : 'text-gray-700 hover:bg-white'}`}
                            >
                                {t('absenceForm.byForfait')}
                            </button>
                        </div>
                    </div>

                    {!isForfait && (
                        <>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t('absenceForm.startDate')}</label>
                                    <input
                                        type="date"
                                        id="date"
                                        name="date"
                                        className="input input-bordered w-full"
                                        value={date}
                                        onChange={(e) => updateFormData('date', e.target.value)}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t('absenceForm.endDate') || t('absenceForm.returnDate')}</label>
                                    <input
                                        type="date"
                                        id="date_end"
                                        name="date_end"
                                        className="input input-bordered w-full"
                                        value={date_end}
                                        min={date || undefined}
                                        onChange={(e) => updateFormData('date_end', e.target.value)}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t('absenceForm.endDateInclusiveHint')}
                                    </p>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">
                                    {t('absenceForm.hoursCount')} {date && date_end && date === date_end ? t('absenceForm.partialAbsenceSuffix') : ""}
                                </label>
                                <input
                                    type="number"
                                    id="hours"
                                    name="hours"
                                    className="input input-bordered w-full"
                                    value={hours}
                                    step="0.5"
                                    min="0"
                                    max="8"
                                    onChange={(e) => updateFormData('hours', parseFloat(e.target.value) || 0)}
                                    disabled={date && date_end && date !== date_end}
                                />
                                {date && date_end && date !== date_end && (
                                    <p className="text-xs text-gray-600 mt-1">
                                        {t('absenceForm.multiDayHoursHint')}
                                    </p>
                                )}
                            </div>

                            <div className="mb-4 p-3 bg-gray-100 rounded-md">
                                <h3 className="font-medium text-sm mb-1">{t('absenceForm.summary')}</h3>
                                <p className="text-sm">{t('absenceForm.totalDays')}: <span className="font-semibold">{calculateDays()}</span></p>
                                <p className="text-xs text-gray-600 mt-1">
                                    {t('absenceForm.autoCalculatedDaysHint')}
                                </p>
                                {!companyConfig.absence_count_weekends && (
                                    <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded mt-2 border border-blue-200">
                                        {t('absenceForm.weekendsExcludedHint')}
                                    </p>
                                )}
                                {!companyConfig.absence_count_public_holidays && (
                                    <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded mt-2 border border-blue-200">
                                        {t('absenceForm.publicHolidaysExcludedHint')}
                                    </p>
                                )}
                            </div>
                        </>
                    )}

                    {isForfait && (
                        <div className="mb-4">
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">{t('absenceForm.forfaitMonth')}</label>
                                <input
                                    type="month"
                                    className="input input-bordered w-full"
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">{t('absenceForm.forfaitDays')}</label>
                                <input
                                    type="number"
                                    name="nombre_jours"
                                    className="input input-bordered w-full"
                                    value={nombreJours}
                                    step="0.5"
                                    min="0.5"
                                    onChange={(e) => setNombreJours(e.target.value)}
                                    required
                                />
                            </div>
                            <p className="text-xs text-gray-500">
                                {t('absenceForm.forfaitHint')}
                            </p>
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-1">{t('absenceForm.description')}</label>
                        <textarea
                            id="description"
                            name="description"
                            className="textarea textarea-bordered w-full"
                            value={description || ""}
                            onChange={(e) => updateFormData('description', e.target.value)}
                            rows="3"
                        ></textarea>
                    </div>

                    <div className="flex justify-end gap-4">
                        <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => setIsDrawerOpen(false)}
                        >
                            {t('basePrimeForm.cancel')}
                        </button>
                        <button type="submit" className="btn btn-outline bg-[--color-primary] text-white">
                            {id ? t('hspForm.update') : t('hspForm.add')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AbsenceForm;
